export const isColorWhite = (color: string): boolean => {
  const normalizedColor = color?.trim().toLowerCase();

  if (/^#([a-f0-9]{3}|[a-f0-9]{6})$/.test(normalizedColor)) {
    // Expand shorthand (#FFF -> #FFFFFF)
    const hex =
      normalizedColor.length === 4
        ? `#${normalizedColor[1].repeat(2)}${normalizedColor[2].repeat(
            2
          )}${normalizedColor[3].repeat(2)}`
        : normalizedColor;

    return hex === "#ffffff";
  }

  const rgbMatch = normalizedColor?.match(
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/
  );

  if (rgbMatch) {
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    const [_, r, g, b] = rgbMatch.map(Number);
    return r === 255 && g === 255 && b === 255;
  }

  return false;
};

export function getTextColorForBackground(
  backgroundColor: string
): "#000" | "#fff" {
  // Convert HEX color to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const cleanedHex = hex.replace("#", "");
    const expandedHex =
      cleanedHex.length === 3
        ? cleanedHex
            .split("")
            .map((char) => char + char)
            .join("")
        : cleanedHex;

    const red = parseInt(expandedHex.substring(0, 2), 16);
    const green = parseInt(expandedHex.substring(2, 4), 16);
    const blue = parseInt(expandedHex.substring(4, 6), 16);

    return [red, green, blue];
  };

  // Calculate relative luminance for a single color channel
  const calculateLuminanceForChannel = (channel: number): number => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };

  // Calculate overall relative luminance
  const calculateRelativeLuminance = (
    rgb: [number, number, number]
  ): number => {
    const [red, green, blue] = rgb;
    return (
      0.2126 * calculateLuminanceForChannel(red) +
      0.7152 * calculateLuminanceForChannel(green) +
      0.0722 * calculateLuminanceForChannel(blue)
    );
  };

  // Calculate contrast ratio between two luminance values
  const calculateContrastRatio = (
    luminance1: number,
    luminance2: number
  ): number => {
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);
    return (lighter + 0.05) / (darker + 0.05);
  };

  // Parse the background color and calculate its luminance
  const rgb = hexToRgb(backgroundColor);
  const backgroundLuminance = calculateRelativeLuminance(rgb);

  // Define luminance values for white and black text
  const whiteLuminance = 1;
  const blackLuminance = 0;

  // Compare contrast ratios and return the best text color
  const contrastWithWhite = calculateContrastRatio(
    backgroundLuminance,
    whiteLuminance
  );
  const contrastWithBlack = calculateContrastRatio(
    backgroundLuminance,
    blackLuminance
  );

  return contrastWithWhite >= contrastWithBlack ? "#fff" : "#000";
}

export const getPlaceholdJPUrl = (
  text: string,
  backgroundColor: string = "000",
  textColor: string = "fff",
  fontSize: number = 16,
  size: string = "150150"
) => {
  backgroundColor = backgroundColor.replace("#", "");
  textColor = textColor.replace("#", "");
  return `https://placehold.jp/${fontSize}/${backgroundColor}/${textColor}/${size}.png?text=${text}`;
};
